import React, { useEffect, useMemo, useState } from "react";
import {
  BadgePercent,
  Check,
  Image as ImageIcon,
  Lightbulb,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  UtensilsCrossed,
} from "lucide-react";
import { toast } from "react-toastify";
import ClientSearchSelect from "../common/ClientSearchSelect";

const normalize = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const parseArrayText = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [value];
  }
};

const getItemId = (item) =>
  String(item?.item_id ?? item?.id ?? item?.pk_item_id ?? "");

const isEnabled = (value) => {
  const normalizedValue = String(value ?? "0")
    .trim()
    .toUpperCase();

  return (
    normalizedValue === "1" ||
    normalizedValue === "Y" ||
    normalizedValue === "YES" ||
    normalizedValue === "TRUE"
  );
};

const getRecommendedValue = (item) => {
  return (
    item?.recomonded ??
    item?.recommended ??
    item?.is_recommended ??
    item?.isRecommended ??
    "0"
  );
};

const getOfferValue = (item) => {
  return item?.offers ?? item?.offer ?? item?.is_offer ?? item?.isOffer ?? "0";
};

const getSuggestionValue = (item) => {
  return item?.suggestion ?? item?.is_suggestion ?? item?.isSuggestion ?? "0";
};

const getVariants = (item) => {
  if (Array.isArray(item?.variant_list)) return item.variant_list;
  if (Array.isArray(item?.prices)) return item.prices;

  return [];
};

const getPrice = (item) => {
  const variants = getVariants(item);

  const prices = variants
    .map((variant) =>
      Number(
        variant?.price ??
          variant?.dining_price ??
          variant?.home_delivery_price ??
          variant?.takeaway_price ??
          variant?.room_order_price ??
          variant?.car_order_price ??
          0,
      ),
    )
    .filter((price) => Number.isFinite(price) && price > 0);

  return prices.length ? Math.min(...prices) : 0;
};

function FeatureToggle({
  active,
  disabled,
  icon,
  label,
  activeClass,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex min-w-0 items-center justify-center gap-1 rounded-md border px-1.5 py-1 text-[9px] font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
        active
          ? activeClass
          : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
      }`}
    >
      {disabled ? <RefreshCw size={9} className="animate-spin" /> : icon}

      <span className="truncate">{label}</span>

      {active && !disabled ? <Check size={9} /> : null}
    </button>
  );
}

function ItemCard({ item, categoryName, updatingKey, onToggle }) {
  const id = getItemId(item);
  const price = getPrice(item);

  const category =
    categoryName || parseArrayText(item?.category)?.[0] || "Uncategorised";

  const variants = getVariants(item);

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="relative h-[142px] overflow-hidden bg-slate-100">
        {item?.photo ? (
          <img
            src={item.photo}
            alt={item?.item_name || "Catalog item"}
            className="h-full w-full object-cover object-center"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-slate-100 text-slate-400">
            <ImageIcon size={22} />
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

        <div className="absolute left-2 top-2 max-w-[70%] truncate rounded-full bg-black/65 px-2 py-0.5 text-[8px] font-bold text-white backdrop-blur">
          {category}
        </div>

        {price > 0 ? (
          <div className="absolute right-2 top-2 rounded-full bg-black/65 px-2 py-0.5 text-[9px] font-black text-white shadow-sm backdrop-blur">
            ₹{price}
          </div>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 p-2">
          <h3 className="line-clamp-1 text-[12px] font-black text-white drop-shadow">
            {item?.item_name || "Unnamed item"}
          </h3>

          <p className="mt-0.5 line-clamp-1 text-[10px] leading-3 text-white/80">
            {item?.item_description || "No description added"}
          </p>

          <div className="mt-1 flex items-center justify-between text-[9px] text-white/80">
            <span>
              {variants.length} variant{variants.length === 1 ? "" : "s"}
            </span>

            <span
              className={
                item?.visibility === "Y" ? "text-emerald-300" : "text-red-300"
              }
            >
              {item?.visibility === "Y" ? "Visible" : "Hidden"}
            </span>
          </div>
        </div>
      </div>

      <div
        className={`grid gap-1 bg-white p-1.5 ${
          variants.length === 1 ? "grid-cols-3" : "grid-cols-2"
        }`}
      >
        <FeatureToggle
          active={isEnabled(item?.recommended)}
          disabled={updatingKey === `${id}-recommended`}
          icon={<Star size={10} />}
          label="Reco"
          activeClass="border-amber-200 bg-amber-50 text-amber-700"
          onClick={() => onToggle(item, "recommended")}
        />

        <FeatureToggle
          active={isEnabled(item?.offers)}
          disabled={updatingKey === `${id}-offers`}
          icon={<BadgePercent size={10} />}
          label="Offer"
          activeClass="border-emerald-200 bg-emerald-50 text-emerald-700"
          onClick={() => onToggle(item, "offers")}
        />

        {variants.length === 1 ? (
          <FeatureToggle
            active={isEnabled(item?.suggestion)}
            disabled={updatingKey === `${id}-suggestion`}
            icon={<Lightbulb size={10} />}
            label="Sug"
            activeClass="border-violet-200 bg-violet-50 text-violet-700"
            onClick={() => onToggle(item, "suggestion")}
          />
        ) : null}
      </div>
    </article>
  );
}

export default function CatalogHighlightsManager({
  userId,
  loading,
  catalogMenus,

  fetchCatalogueItemsMaster,

  updateCatalogRecommended,
  updateCatalogOffers,
  updateCatalogSuggestion,

  clients = [],
  selectedClientCode = "",
  onClientChange,
}) {
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [updatingKey, setUpdatingKey] = useState("");
  const [localFeatureMap, setLocalFeatureMap] = useState({});
const [itemsLoading, setItemsLoading] = useState(false);

  const loadData = async () => {
    if (!userId) return;

    setItemsLoading(true);

    try {
      await fetchCatalogueItemsMaster?.(userId);
    } catch (error) {
      console.error("Catalogue master load error:", error);
      toast.error("Failed to load catalogue items");
    } finally {
      setItemsLoading(false);
    }
  };

useEffect(() => {
  setSearch("");
  setCategoryId("all");
  setActiveFilter("all");
  setLocalFeatureMap({});

  if (userId) {
    loadData();
  }
}, [userId]);

  const itemRows = useMemo(() => {
    const seen = new Set();
    const rows = [];

    (catalogMenus || []).forEach((menu) => {
      const items = Array.isArray(menu?.menu_items)
        ? menu.menu_items
        : Array.isArray(menu?.item_list)
          ? menu.item_list
          : [];

      items.forEach((item) => {
        const id = getItemId(item);

        if (!id || seen.has(id)) return;

        seen.add(id);

        const localStatus = localFeatureMap[id] || {};

        const recommendedValue =
          localStatus.recommended ??
          (isEnabled(getRecommendedValue(item)) ? "1" : "0");

        const offersValue =
          localStatus.offers ?? (isEnabled(getOfferValue(item)) ? "1" : "0");

        const suggestionValue =
          localStatus.suggestion ??
          (isEnabled(getSuggestionValue(item)) ? "1" : "0");

        rows.push({
          ...item,

          recomonded: recommendedValue,
          recommended: recommendedValue,

          offers: offersValue,
          suggestion: suggestionValue,

          __categoryId: String(menu?.cat_id || menu?.category_id || ""),
          __categoryName:
            menu?.category_name ||
            menu?.sub_category ||
            menu?.categoryName ||
            "Uncategorised",
        });
      });
    });

    return rows;
  }, [catalogMenus, localFeatureMap]);

  const filteredItems = useMemo(() => {
    const query = normalize(search);

    return itemRows.filter((item) => {
      if (
        categoryId !== "all" &&
        String(item.__categoryId || "") !== String(categoryId || "")
      ) {
        return false;
      }

      if (activeFilter === "recommended" && !isEnabled(item.recommended)) {
        return false;
      }

      if (activeFilter === "offers" && !isEnabled(item.offers)) {
        return false;
      }

      if (activeFilter === "suggestions" && !isEnabled(item.suggestion)) {
        return false;
      }

      if (!query) return true;

      return normalize(
        [
          item.item_name,
          item.item_description,
          item.__categoryName,
          item.item_id,
          item.id,
        ]
          .filter(Boolean)
          .join(" "),
      ).includes(query);
    });
  }, [itemRows, categoryId, activeFilter, search]);

  const stats = {
    all: itemRows.length,
    recommended: itemRows.filter((item) => isEnabled(item.recommended)).length,
    offers: itemRows.filter((item) => isEnabled(item.offers)).length,
    suggestions: itemRows.filter((item) => isEnabled(item.suggestion)).length,
  };

  const categoryOptions = useMemo(() => {
    const seen = new Set();

    return (catalogMenus || [])
      .map((menu, index) => {
        const id = String(menu?.cat_id || menu?.category_id || "").trim();
        const name =
          menu?.category_name ||
          menu?.sub_category ||
          menu?.categoryName ||
          "Category";

        if (!id) {
          return {
            id: `empty-${index}`,
            value: "",
            name,
            key: `empty-${index}`,
          };
        }

        if (seen.has(id)) {
          return null;
        }

        seen.add(id);

        return {
          id,
          value: id,
          name,
          key: `category-${id}`,
        };
      })
      .filter(Boolean);
  }, [catalogMenus]);

  const isApiSuccess = (result) => {
    if (!result) return false;

    const responseCode =
      result?.responseCode ||
      result?.data?.responseCode ||
      result?.response?.responseCode;

    if (responseCode !== undefined && responseCode !== null) {
      return String(responseCode) === "00" || String(responseCode) === "0";
    }

    if (result?.success !== undefined) {
      return Boolean(result.success);
    }

    return true;
  };

  const getApiMessage = (result) => {
    return (
      result?.responseMessage ||
      result?.message ||
      result?.data?.responseMessage ||
      result?.data?.message ||
      result?.response?.responseMessage ||
      result?.response?.message ||
      ""
    );
  };

  const getFeatureLabel = (type) => {
    if (type === "recommended") return "Recommended";
    if (type === "offers") return "Offer";
    return "Suggestion";
  };

 const handleToggle = async (item, type) => {
   const id = getItemId(item);

   if (!id) {
     toast.error("Item ID missing");
     return;
   }

   if (!userId) {
     toast.error("Please select client first");
     return;
   }

   const field = type === "suggestion" ? "suggestion" : type;

   const currentRawValue =
     type === "recommended"
       ? (item?.recommended ?? item?.recomonded)
       : type === "offers"
         ? item?.offers
         : item?.suggestion;

   const currentValue = isEnabled(currentRawValue) ? "1" : "0";
   const nextValue = currentValue === "1" ? "0" : "1";
   const featureLabel = getFeatureLabel(type);

   setUpdatingKey(`${id}-${type}`);

   setLocalFeatureMap((prev) => ({
     ...prev,
     [id]: {
       ...(prev[id] || {}),
       [field]: nextValue,
     },
   }));

   try {
     let result = null;

     if (type === "recommended") {
       result = await updateCatalogRecommended({
         id,
         userId,
         recommended: nextValue,
       });
     } else if (type === "offers") {
       result = await updateCatalogOffers({
         id,
         userId,
         offers: nextValue,
       });
     } else {
       result = await updateCatalogSuggestion({
         id,
         userId,
         suggestion: nextValue,
       });
     }

     if (!isApiSuccess(result)) {
       setLocalFeatureMap((prev) => ({
         ...prev,
         [id]: {
           ...(prev[id] || {}),
           [field]: currentValue,
         },
       }));

       toast.error(getApiMessage(result) || `${featureLabel} update failed`);
       return;
     }

   } catch (error) {
     console.error(`${featureLabel} update error:`, error);

     setLocalFeatureMap((prev) => ({
       ...prev,
       [id]: {
         ...(prev[id] || {}),
         [field]: currentValue,
       },
     }));

     toast.error(`${featureLabel} update failed`);
   } finally {
     setUpdatingKey("");
   }
 };

  const filters = [
    {
      key: "all",
      label: "All Items",
      icon: <UtensilsCrossed size={16} />,
    },
    {
      key: "recommended",
      label: "Recommended",
      icon: <Star size={16} />,
    },
    {
      key: "offers",
      label: "Offers",
      icon: <BadgePercent size={16} />,
    },
    {
      key: "suggestions",
      label: "Suggestions",
      icon: <Lightbulb size={16} />,
    },
  ];

  return (
    <div className="min-h-0 space-y-3">
      <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex w-full flex-nowrap items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            <Sparkles size={16} className="shrink-0 text-emerald-600" />

            <div className="min-w-0">
              <h2 className="truncate text-sm font-black text-slate-900">
                Catalog Highlights
              </h2>

              <p className="truncate text-[11px] text-slate-500">
                Recommended, offers and suggestions control
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <ClientSearchSelect
              clients={clients}
              value={selectedClientCode}
              onChange={onClientChange}
              placeholder={
                clients.length === 0 ? "No clients" : "Select client"
              }
              searchPlaceholder="Search client by name or code..."
              className="w-[210px]"
              allowClear
            />

            <button
              type="button"
              onClick={loadData}
              disabled={itemsLoading || loading || !userId}
              className="inline-flex h-8 items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                size={13}
                className={itemsLoading || loading ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>
        </div>

        {userId ? (
          <div className="mt-3 flex w-full flex-nowrap items-center gap-2 overflow-x-auto pb-1">
            {filters.map((filter) => (
              <button
                type="button"
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2 text-[11px] font-bold transition ${
                  activeFilter === filter.key
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                <span className="flex shrink-0 items-center">
                  {filter.icon}
                </span>

                <span className="whitespace-nowrap">{filter.label}</span>

                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-black text-slate-700">
                  {stats[filter.key]}
                </span>
              </button>
            ))}

            <div className="flex h-8 w-[350px] shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 focus-within:border-emerald-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-emerald-100">
              <Search size={14} className="shrink-0 text-slate-400" />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search item..."
                className="w-full bg-transparent text-[11px] outline-none"
              />
            </div>

            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="h-8 w-[170px] shrink-0 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">All Categories</option>

              {categoryOptions.map((category) => (
                <option key={category.key} value={category.value}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </section>

      {!userId ? (
        <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <div>
            <UtensilsCrossed className="mx-auto text-slate-400" size={38} />

            <h3 className="mt-4 text-lg font-black text-slate-900">
              Select a client first
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Catalog highlights data will load after selecting a client from
              the top right dropdown.
            </p>
          </div>
        </div>
      ) : (itemsLoading || loading) && itemRows.length === 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={`catalog-shimmer-${index}`}
              className="h-[220px] animate-pulse rounded-2xl bg-slate-200"
            />
          ))}
        </div>
      ) : filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {filteredItems.map((item) => (
            <ItemCard
              key={getItemId(item)}
              item={item}
              categoryName={item.__categoryName}
              updatingKey={updatingKey}
              onToggle={handleToggle}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Sparkles className="mx-auto text-slate-400" size={34} />

          <h3 className="mt-4 text-lg font-black text-slate-900">
            No matching catalog items
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Try another filter, category, or search term.
          </p>
        </div>
      )}
    </div>
  );
}
